import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import {
  FormInstanceDetail,
  FormInstanceSummary,
  FormInstanceStatus,
  FormSubmissionStatus,
} from '@/types'
import * as formInstanceApi from '../../services/api/formInstances'

export interface FormInstanceState {
  instances: FormInstanceSummary[]
  activeInstance: FormInstanceDetail | null
  loading: boolean
  error: string | null
  lockUpdating: boolean
}

const initialState: FormInstanceState = {
  instances: [],
  activeInstance: null,
  loading: false,
  error: null,
  lockUpdating: false,
}

const resolveErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message
  }
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: unknown }).message
    if (typeof message === 'string' && message.length > 0) {
      return message
    }
  }
  return fallback
}

export const fetchProjectInstances = createAsyncThunk(
  'formInstances/fetchProject',
  async (projectId: string, thunkAPI) => {
    try {
      const response = await formInstanceApi.listInstances(projectId)
      return response.data?.instances as FormInstanceSummary[]
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '讀取表單任務失敗'))
    }
  },
)

export const createFormInstanceThunk = createAsyncThunk(
  'formInstances/create',
  async (
    payload: Partial<FormInstanceDetail> & { templateId: string; title: string },
    thunkAPI,
  ) => {
    try {
      const response = await formInstanceApi.createInstance(payload)
      return response.data?.instance as FormInstanceDetail
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '建立表單實例失敗'))
    }
  },
)

export const updateFormInstanceThunk = createAsyncThunk(
  'formInstances/update',
  async (
    args: { instanceId: string; data: Partial<FormInstanceDetail> },
    thunkAPI,
  ) => {
    try {
      const response = await formInstanceApi.updateInstance(args.instanceId, args.data)
      return response.data?.instance as FormInstanceDetail
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '更新表單實例失敗'))
    }
  },
)

export const submitFormInstanceThunk = createAsyncThunk(
  'formInstances/submit',
  async (
    args: {
      instanceId: string
      data: {
        status?: FormSubmissionStatus
        reviewNotes?: string | null
        metadata?: Record<string, unknown>
        location?: Record<string, unknown>
        values?: Array<{
          fieldId: string
          valueString?: string | null
          valueNumber?: string | number | null
          valueBoolean?: boolean | null
          valueDate?: string | null
          valueJson?: unknown
          anomalyLevel?: string | null
        }>
      }
    },
    thunkAPI,
  ) => {
    try {
      await formInstanceApi.submitInstance(args.instanceId, args.data)
      return args.instanceId
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '提交表單失敗'))
    }
  },
)

export const toggleInstanceLockThunk = createAsyncThunk(
  'formInstances/toggleLock',
  async (args: { instanceId: string; locked: boolean }, thunkAPI) => {
    try {
      const response = await formInstanceApi.toggleInstanceLock(args.instanceId, args.locked)
      return { instanceId: args.instanceId, locked: response.data?.locked ?? args.locked }
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '更新鎖定狀態失敗'))
    }
  },
)

export const reviewSubmissionThunk = createAsyncThunk(
  'formInstances/review',
  async (
    args: { instanceId: string; submissionId: string; action: 'approve' | 'reject' | 'request_changes'; notes?: string | null },
    thunkAPI,
  ) => {
    try {
      await formInstanceApi.reviewSubmission(args.instanceId, args.submissionId, {
        action: args.action,
        notes: args.notes,
      })
      return args
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '審核表單失敗'))
    }
  },
)

export const fetchFormInstanceDetail = createAsyncThunk(
  'formInstances/fetchDetail',
  async (instanceId: string, thunkAPI) => {
    try {
      const response = await formInstanceApi.getInstanceDetail(instanceId)
      return response.data as FormInstanceDetail
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '讀取表單內容失敗'))
    }
  },
)

export const deleteFormInstanceThunk = createAsyncThunk(
  'formInstances/delete',
  async (instanceId: string, thunkAPI) => {
    try {
      await formInstanceApi.deleteInstance(instanceId)
      return instanceId
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '刪除表單任務失敗'))
    }
  },
)

const formInstanceSlice = createSlice({
  name: 'formInstances',
  initialState,
  reducers: {
    receiveReviewNotification(
      state,
      action: PayloadAction<{ instanceId: string; decision: 'approve' | 'reject' | 'request_changes' }>,
    ) {
      const { instanceId, decision } = action.payload
      if (state.activeInstance && state.activeInstance.id === instanceId) {
        state.activeInstance = {
          ...state.activeInstance,
          status:
            decision === 'approve'
              ? FormInstanceStatus.COMPLETED
              : decision === 'request_changes'
              ? FormInstanceStatus.IN_PROGRESS
              : FormInstanceStatus.PENDING,
        }
      }

      state.instances = state.instances.map((instance) =>
        instance.id === instanceId
          ? {
              ...instance,
              status:
                decision === 'approve'
                  ? FormInstanceStatus.COMPLETED
                  : decision === 'request_changes'
                  ? FormInstanceStatus.IN_PROGRESS
                  : FormInstanceStatus.PENDING,
            }
          : instance,
      )
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createFormInstanceThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createFormInstanceThunk.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload) {
          state.instances.push(action.payload)
          state.activeInstance = action.payload
        }
      })
      .addCase(createFormInstanceThunk.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload as string) || action.error.message || '建立表單失敗'
      })
      .addCase(fetchProjectInstances.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchProjectInstances.fulfilled, (state, action) => {
        state.loading = false
        state.instances = action.payload
      })
      .addCase(fetchProjectInstances.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload as string) || action.error.message || '讀取表單任務失敗'
      })
      .addCase(fetchFormInstanceDetail.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchFormInstanceDetail.fulfilled, (state, action) => {
        state.loading = false
        state.activeInstance = action.payload
      })
      .addCase(fetchFormInstanceDetail.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload as string) || action.error.message || '讀取表單內容失敗'
      })
      .addCase(deleteFormInstanceThunk.fulfilled, (state, action) => {
        state.instances = state.instances.filter(instance => instance.id !== action.payload)
        if (state.activeInstance?.id === action.payload) {
          state.activeInstance = null
        }
      })
      .addCase(deleteFormInstanceThunk.rejected, (state, action) => {
        state.error = (action.payload as string) || action.error.message || '刪除表單任務失敗'
      })
      .addCase(updateFormInstanceThunk.fulfilled, (state, action) => {
        if (!action.payload) return
        state.instances = state.instances.map(instance =>
          instance.id === action.payload.id ? action.payload : instance,
        )
        if (state.activeInstance?.id === action.payload.id) {
          state.activeInstance = {
            ...state.activeInstance,
            ...action.payload,
          }
        }
      })
      .addCase(submitFormInstanceThunk.fulfilled, (state, action) => {
        const instanceId = action.payload
        state.instances = state.instances.map(instance =>
          instance.id === instanceId
            ? { ...instance, status: FormInstanceStatus.IN_PROGRESS }
            : instance,
        )
        if (state.activeInstance?.id === instanceId) {
          state.activeInstance = {
            ...state.activeInstance,
            status: FormInstanceStatus.IN_PROGRESS,
          }
        }
      })
      .addCase(toggleInstanceLockThunk.pending, (state) => {
        state.lockUpdating = true
        state.error = null
      })
      .addCase(toggleInstanceLockThunk.fulfilled, (state, action) => {
        state.lockUpdating = false
        const { instanceId, locked } = action.payload
        if (state.activeInstance?.id === instanceId) {
          const metadata = (state.activeInstance.metadata ?? {}) as Record<string, unknown>
          state.activeInstance = {
            ...state.activeInstance,
            metadata: {
              ...metadata,
              locked,
              lockedAt: locked ? new Date().toISOString() : null,
            },
          }
        }
      })
      .addCase(toggleInstanceLockThunk.rejected, (state, action) => {
        state.lockUpdating = false
        state.error = (action.payload as string) || action.error.message || '更新鎖定狀態失敗'
      })
  },
})

export const { receiveReviewNotification } = formInstanceSlice.actions
export default formInstanceSlice.reducer
